import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  ListGroup,
  ListGroupItem,
  Badge,
  Row,
  Col,
} from "reactstrap";
import { FaSearch, FaUser, FaTimes, FaCheck, FaMoneyBillWave, FaKeyboard } from "react-icons/fa";
import POSKeypadModal from "./POSKeypadModal";

const ClientSelectModal = ({
  isOpen,
  toggle,
  clients = [],
  selectedClient,
  onSelect,
  onAddNew,
}) => {
  const [query, setQuery] = useState("");
  const [keypadOpen, setKeypadOpen] = useState(false);

  // ✅ Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery("");
    }
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    if (!q) return clients;

    return clients.filter((c) =>
      (c.label || "").toLowerCase().includes(q)
    );
  }, [query, clients]);

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl" centered className="pos-client-modal">
        <ModalHeader toggle={toggle} className="fw-bold">
          <FaUser className="me-2" />
          Sélection Client
        </ModalHeader>

        <ModalBody className="p-3">
          {/* SEARCH */}
          <div className="d-flex gap-2 mb-3">
            <div className="flex-grow-1 position-relative">
              <Input
                type="text"
                value={query}
                readOnly
                inputMode="none"
                onKeyDown={(e) => e.preventDefault()}
                className="ps-5 py-2 rounded-3 fw-bold pos-nokeyboard"
                placeholder="Rechercher un client..."
                onClick={() => setKeypadOpen(true)}  // ✅ open keypad
              />

              <FaSearch
                style={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.7,
                }}
              />

              <Button
                type="button"
                color="light"
                className="position-absolute top-50 translate-middle-y border rounded-3"
                style={{ right: 10 }}
                onClick={() => setKeypadOpen(true)}   // ✅ open keypad button
                title="Clavier"
              >
                <FaKeyboard />
              </Button>
            </div>

            {onAddNew && (
              <Button color="primary" className="fw-bold px-3" onClick={onAddNew}>
                + Nouveau
              </Button>
            )}

            <Button
              color="secondary"
              outline
              className="fw-bold px-3"
              onClick={() => setQuery("")}
              disabled={!query}
            >
              <FaTimes className="me-2" />
              Effacer
            </Button>
          </div>

          {/* SELECTED CLIENT QUICK VIEW */}
          {selectedClient && (
            <div className="p-3 mb-3 rounded-4 bg-light border" style={{ fontSize: 15 }}>
              <Row>
                <Col md={6}>
                  <div className="fw-bold">
                    Client choisi :{" "}
                    <span className="text-primary">{selectedClient.label}</span>
                  </div>
                </Col>
                <Col md={6} className="text-end">
                  <Badge color="success" pill>
                    <FaCheck className="me-1" />
                    Sélectionné
                  </Badge>
                </Col>
              </Row>
            </div>
          )}

          {/* LIST */}
          <ListGroup style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div className="text-center text-muted py-5">Aucun client trouvé</div>
            ) : (
              filtered.map((c) => {
                const active = selectedClient?.value === c.value;
                const solde = (Number(c.totalCredit ?? 0) - Number(c.totalDebit ?? 0)).toFixed(2);

                return (
                  <ListGroupItem
                    key={c.value}
                    action
                    onClick={() => {
                      onSelect(c);
                      toggle();
                    }}
                    className={`p-3 rounded-4 mb-2 border ${active ? "border-primary" : ""}`}
                    style={{
                      cursor: "pointer",
                      background: active ? "#eef5ff" : "white",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{c.label}</div>
                      {active && (
                        <Badge color="primary" pill>
                          <FaCheck className="me-1" />
                          OK
                        </Badge>
                      )}
                    </div>

                    <div className="d-flex justify-content-between mt-2 text-muted">
                      <div>
                        <FaMoneyBillWave className="me-2" />
                        Solde : <strong>{solde} DH</strong>
                      </div>
                      <div className="text-end">
                        <small>Débit : {c.totalDebit ?? 0}</small> |{" "}
                        <small>Crédit : {c.totalCredit ?? 0}</small>
                      </div>
                    </div>
                  </ListGroupItem>
                );
              })
            )}
          </ListGroup>

          {/* CLEAR SELECTION */}
          <div className="d-flex justify-content-end mt-3">
            <Button
              color="danger"
              outline
              className="fw-bold"
              onClick={() => {
                onSelect(null);
                toggle();
              }}
            >
              Désélectionner client
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* ✅ POS KEYPAD */}
      <POSKeypadModal
        isOpen={keypadOpen}
        toggle={() => setKeypadOpen(false)}
        value={query}
        onChange={(val) => setQuery(val)} // ✅ instant filtering
        onSearch={() => setKeypadOpen(false)}
        title="Recherche Client"
      />
    </>
  );
};

export default ClientSelectModal;
